import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useState } from "react";

import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "@mui/material";
import clsx from "clsx";
import { DateTime } from "luxon";
import { Clipboard } from "react-feather";
import { Link as RouterLink } from "react-router-dom";
import mainStyles from "styles";

import { SubscriptionSeatTypes } from "@safetyculture/s12-apis-connect-web/s12/common/seat_types_pb";
import { UserByIdentity } from "@safetyculture/s12-apis-connect-web/s12/identity/admin/v1/identity_management_pb";
import { RoleType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { listUsersByIdentityID } from "api/endpoints/identity";
import { listUsersLastSeen } from "api/endpoints/organisation";
import { getSsoConfigurationAdmin } from "api/endpoints/sso";
import { getUser, getUserOrgs } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import ActiveStatusChip from "components/shared/chip/active-status-chip";
import RegionChip from "components/shared/chip/region-chip";
import SubscriptionTypeChip from "components/shared/chip/subscription-type-chip";
import UserSeatTypeChip from "components/shared/chip/user-seat-type-chip";
import EmptyTableResults from "components/shared/table/empty-table-results";
import { DEFAULT_REGION_CODE, LoginType } from "constants/index";
import { IdentityContext } from "context/identity-context";
import { SnackbarContext } from "context/snackbar-context";
import { mapRegionStringToRegionCode } from "helpers/env-values-helper";
import { getUserLastSeen } from "helpers/get-user-last-seen";
import { toUUID } from "helpers/s12-helpers";
import { convertTimestampToDate } from "helpers/time";
import { IdentityUserDetail, IdentityUsersTabProps } from "types/identity";

const IdentityUsersTab = ({
  shouldLoad = true,
  identityID,
  email,
}: IdentityUsersTabProps) => {
  const classes = mainStyles();
  const theme = useTheme();

  const { identity } = useContext(IdentityContext);
  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const [userDetails, setUserDetails] = useState<IdentityUserDetail[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  const getEmptyUserDetail = (user: UserByIdentity) => {
    return {
      id: user.id,
      firstName: "",
      lastName: "",
      createdAt: undefined,
      lastSeen: undefined,
      permissionsSetName: undefined,
      seatType: SubscriptionSeatTypes.SUBSCRIPTION_SEAT_TYPE_UNSPECIFIED,
      active: undefined,
      orgId: user.orgId,
      orgName: "",
      orgSubscription: undefined,
      orgActive: undefined,
      region: mapRegionStringToRegionCode(user.region),
      loginType: LoginType.UNSPECIFIED,
      jitEnabled: undefined,
    };
  };

  const getUserDetail = async (user: UserByIdentity) => {
    const userDetail: IdentityUserDetail = getEmptyUserDetail(user);
    const regionCode = mapRegionStringToRegionCode(user.region);

    await getUser({ userId: user.id }, callOptionsHeaders(regionCode, user.id))
      .then((userResp) => {
        if (userResp) {
          userDetail.firstName = userResp.firstName;
          userDetail.lastName = userResp.lastName;
          userDetail.createdAt = userResp.createdAt;
          userDetail.permissionsSetName =
            userResp.permissionsSetIdentifier?.name;
          userDetail.seatType = userResp.seatType;
        }
      })
      .catch(showError);

    await getUserOrgs(
      { userId: toUUID(user.id) },
      callOptionsHeaders(regionCode, user.id)
    )
      .then((resp) => {
        for (const org of resp.organisations) {
          if (org.type == RoleType.ORG) {
            userDetail.orgId = org.id;
            userDetail.orgName = org.name;
            userDetail.orgSubscription = org.subscription[0]?.type;
            userDetail.orgActive = org.orgFlags?.active;
            userDetail.active = !org.inactiveMembers.includes(user.id);
            userDetail.region = regionCode;
            break;
          }
        }
      })
      .catch(showError);

    await listUsersLastSeen(
      {
        orgId: user.orgId,
        userIds: [user.id],
      },
      callOptionsHeaders(regionCode, user.orgId)
    )
      .then((resp) => {
        if (resp.users.length > 0 && resp.users[0].lastSeen) {
          userDetail.lastSeen = resp.users[0].lastSeen;
        }
      })
      .catch(showError);

    await getSsoConfigurationAdmin(
      { orgId: user.orgId },
      callOptionsHeaders(regionCode, user.orgId)
    )
      .then((resp) => {
        const ssoConf = resp.authConf?.ssoConf;
        const pwdConf = resp.authConf?.pwdConf;
        userDetail.jitEnabled = ssoConf?.jitProvisioningEnabled;

        if (ssoConf?.ssoEnabled && ssoConf?.enforceSsoForDomains) {
          userDetail.loginType =
            LoginType.PASSWORD_AND_SSO_ENFORCED_FOR_DOMAINS;
        } else if (ssoConf?.ssoEnabled && pwdConf?.pwdEnabled) {
          userDetail.loginType = LoginType.ANY;
        } else if (ssoConf?.ssoEnabled) {
          userDetail.loginType = LoginType.SSO;
        } else {
          userDetail.loginType = LoginType.PASSWORD;
        }
      })
      .catch(showError);

    return userDetail;
  };

  const getUsersForIdentity = async () => {
    setIsLoadingUsers(true);

    const allUsers = await listUsersByIdentityID(
      { identityId: identity?.identityId ?? identityID },
      callOptionsHeaders(
        DEFAULT_REGION_CODE,
        identity?.identityId ?? identityID
      )
    ).catch(showError);

    if (!allUsers) {
      return;
    }

    const allUserDetails: IdentityUserDetail[] = [];
    await Promise.all(
      allUsers.users.map((user) => {
        return getUserDetail(user).then((userDetail) => {
          allUserDetails.push(userDetail);
        });
      })
    );

    const sortedUserDetails = allUserDetails.sort(
      (a, b) =>
        // Sort by org name, then org ID
        a.orgName.localeCompare(b.orgName) || a.orgId.localeCompare(b.orgId)
    );

    setUserDetails(sortedUserDetails);
  };

  useEffect(() => {
    if (shouldLoad) {
      if (identity?.identityId || identityID) {
        getUsersForIdentity().finally(() => setIsLoadingUsers(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad, identity, identityID]);

  const displayCreationDate = (createdAt: Timestamp | undefined) => {
    if (createdAt) {
      const createdAtDate = convertTimestampToDate(createdAt);
      const formattedDate =
        DateTime.fromJSDate(createdAtDate).toFormat("dd MMM yyyy");
      const timeSinceCreation = DateTime.fromJSDate(createdAtDate).toRelative();

      return `${formattedDate} (${timeSinceCreation})`;
    }
    return "Unknown";
  };

  const IdentityUserRow = (user: IdentityUserDetail) => {
    return (
      <TableRow key={user.id}>
        <TableCell>
          <Link
            component={RouterLink}
            to={`/user/${user.region}/${user.id}`}
            sx={classes.removeLinkStyle}
            data-testid={`identity-user-link-${user.id}`}
          >
            <Typography
              variant="body2"
              display="inline"
              color="primary"
              style={{
                fontWeight: "bold",
                fontSize: "1rem",
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
              data-testid={`identity-user-name-${user.id}`}
            >
              {user.firstName} {user.lastName}
            </Typography>
          </Link>
          <div>
            <span data-testid={`identity-user-active-chip-${user.id}`}>
              <ActiveStatusChip active={user.active} />
            </span>
            <span data-testid={`identity-user-seat-chip-${user.id}`}>
              <UserSeatTypeChip seatType={user.seatType} />
            </span>
          </div>
          <div onClick={() => copyToClipboard(user.id)}>
            <Typography
              variant="body2"
              sx={classes.clickable}
              data-testid={`identity-user-id-${user.id}`}
            >
              {user.id}{" "}
              <Clipboard
                color={theme.palette.text.primary}
                size={"1rem"}
                style={{ position: "relative", top: 2 }}
              />
            </Typography>
          </div>
        </TableCell>
        <TableCell>
          <Link
            component={RouterLink}
            to={`/organisation/${user.region}/${user.orgId}`}
            sx={classes.removeLinkStyle}
            data-testid={`identity-user-org-link-${user.orgId}`}
          >
            <Typography
              variant="body2"
              display="inline"
              color="primary"
              style={{
                fontWeight: "bold",
                fontSize: "1rem",
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
              data-testid={`identity-user-org-name-${user.orgId}`}
            >
              {user.orgName}
            </Typography>
          </Link>
          <div>
            <span data-testid={`identity-user-org-region-chip-${user.orgId}`}>
              <RegionChip regionCode={user.region} />
            </span>
            <span data-testid={`identity-user-org-active-chip-${user.orgId}`}>
              <ActiveStatusChip active={user.orgActive} />
            </span>
            <span
              data-testid={`identity-user-org-subscription-chip-${user.orgId}`}
            >
              <SubscriptionTypeChip subscriptionType={user.orgSubscription} />
            </span>
          </div>
          <div onClick={() => copyToClipboard(user.orgId)}>
            <Typography
              variant="body2"
              sx={classes.clickable}
              data-testid={`identity-user-org-id-${user.orgId}`}
            >
              {user.orgId}{" "}
              <Clipboard
                color={theme.palette.text.primary}
                size={"1rem"}
                style={{ position: "relative", top: 2 }}
              />
            </Typography>
          </div>
        </TableCell>
        <TableCell data-testid={`identity-user-permissions-set-${user.id}`}>
          {user.permissionsSetName ?? "N/A"}
        </TableCell>
        <TableCell>
          <b>Last Seen: </b>
          <span data-testid={`identity-user-last-seen-${user.id}`}>
            {getUserLastSeen(user.lastSeen)}
          </span>
          <br />
          <b>Registered on: </b>
          <span data-testid={`identity-user-created-at-${user.id}`}>
            {displayCreationDate(user.createdAt)}
          </span>
          <br />
          <b>Login type: </b>
          <span data-testid={`identity-user-login-type-${user.id}`}>
            {user.loginType}
            {user.jitEnabled && " (JIT enabled)"}
          </span>
        </TableCell>
        <TableCell>
          {(identity?.email || email) && (
            <Link
              component={RouterLink}
              to={{
                pathname: "/sudo",
                state: {
                  regionCode: user.region,
                  userId: user.id,
                  userName: `${user.firstName} ${user.lastName}`,
                  userEmail: `${identity?.email ?? email}`,
                },
              }}
              sx={classes.removeLinkStyle}
            >
              <Button
                variant="contained"
                color="secondary"
                size="large"
                className={clsx(classes.spaceRightMedium)}
                data-testid={`identity-user-sudo-btn-${user.id}`}
              >
                Sudo
              </Button>
            </Link>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table data-testid="identity-users-table">
        <TableBody>
          <TableRow>
            <TableCell>
              <b>User</b>
            </TableCell>
            <TableCell>
              <b>Organisation</b>
            </TableCell>
            <TableCell>
              <b>Permissions Set</b>
            </TableCell>
            <TableCell>
              <b>Login</b>
            </TableCell>
            <TableCell>
              <b>Actions</b>
            </TableCell>
          </TableRow>
          {isLoadingUsers && (
            <TableRow>
              <TableCell colSpan={7}>
                <LinearProgress data-testid="progress-bar" />
              </TableCell>
            </TableRow>
          )}
          {!isLoadingUsers && userDetails.length === 0 && (
            <EmptyTableResults tableHeading="identity users" />
          )}
          {!isLoadingUsers &&
            userDetails.length > 0 &&
            userDetails.map((user) => IdentityUserRow(user))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IdentityUsersTab;
